"use client";

import { ExternalLink, Pin, Calendar, Users, Bookmark, Quote, Eye } from "lucide-react";
import { decodeHtmlEntities } from "@/lib/decode-entities";

const TYPE_STYLES: Record<string, { label: string; className: string }> = {
  "meta-analysis": {
    label: "Meta-analysis",
    className: "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  },
  guideline: {
    label: "Guideline",
    className: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  },
  rct: {
    label: "RCT",
    className: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  },
  review: {
    label: "Review",
    className: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  },
  original: {
    label: "Original",
    className: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
  },
  "case-report": {
    label: "Case Report",
    className: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400",
  },
};

export interface PaperCardProps {
  pmid: string;
  title: string;
  authors: { name: string }[];
  journal: string;
  publishDate: string;
  doi: string;
  articleType: string;
  relevanceScore: number;
  oneLineSummary?: string;
  isPinned: boolean;
  compact?: boolean;
  isBookmarked?: boolean;
  onToggleBookmark?: (pmid: string) => void;
  citationCount?: number;
  viewCount?: number;
  bookmarkCount?: number;
}

function trackView(pmid: string) {
  fetch(`/api/papers/${pmid}/view`, { method: "POST" }).catch(() => {});
}

export function PaperCard({
  pmid,
  title,
  authors,
  journal,
  publishDate,
  doi,
  articleType,
  isPinned,
  compact = false,
  isBookmarked,
  onToggleBookmark,
  citationCount,
  viewCount,
}: PaperCardProps) {
  const typeStyle = TYPE_STYLES[articleType] || TYPE_STYLES.original;
  const parsedDate = publishDate ? new Date(publishDate) : null;
  const year = parsedDate && !isNaN(parsedDate.getTime()) ? String(parsedDate.getFullYear()) : "";
  const authorStr =
    authors.length > 3
      ? `${authors[0].name} et al.`
      : authors.map((a) => a.name).join(", ");

  const pubmedUrl = `https://pubmed.ncbi.nlm.nih.gov/${pmid}/`;
  const doiUrl = doi ? `https://doi.org/${doi}` : null;

  if (compact) {
    return (
      <div className="group flex items-start gap-2 py-2 border-b border-border/50 last:border-0">
        {isPinned && <Pin className="w-3 h-3 mt-1 text-amber-500 shrink-0" />}
        <div className="min-w-0 flex-1">
          <a
            href={pubmedUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackView(pmid)}
            className="text-sm font-medium text-foreground hover:text-primary leading-tight line-clamp-2"
          >
            {title}
          </a>
          <div className="flex items-center gap-2 mt-0.5 text-xs text-muted-foreground">
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${typeStyle.className}`}>
              {typeStyle.label}
            </span>
            <span>{journal}</span>
            <span>{year}</span>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => onToggleBookmark?.(pmid)}
            className={`p-0.5 transition-opacity ${onToggleBookmark ? "opacity-0 group-hover:opacity-100" : "hidden"}`}
            title={isBookmarked ? "북마크 해제" : "북마크"}
          >
            <Bookmark
              className={`w-3.5 h-3.5 ${
                isBookmarked
                  ? "fill-primary text-primary"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            />
          </button>
          <a
            href={pubmedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-card p-4 hover:border-primary/30 transition-colors">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeStyle.className}`}
            >
              {typeStyle.label}
            </span>
            {isPinned && (
              <span className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                <Pin className="w-3 h-3" /> Key Paper
              </span>
            )}
          </div>

          <a
            href={pubmedUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackView(pmid)}
            className="text-sm font-semibold text-foreground hover:text-primary leading-snug block"
          >
            {title}
          </a>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-muted-foreground">
            {authorStr && (
              <span className="flex items-center gap-1">
                <Users className="w-3 h-3" />
                {authorStr}
              </span>
            )}
            <span className="font-medium">{journal}</span>
            {year && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {year}
              </span>
            )}
            {typeof citationCount === "number" && citationCount > 0 && (
              <span className="flex items-center gap-1" title="피인용 횟수 (NIH iCite)">
                <Quote className="w-3 h-3" />
                {citationCount.toLocaleString()}
              </span>
            )}
            {typeof viewCount === "number" && viewCount > 0 && (
              <span className="flex items-center gap-1" title="이 사이트 내 조회수">
                <Eye className="w-3 h-3" />
                {viewCount.toLocaleString()}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-1 shrink-0">
          {onToggleBookmark && (
            <button
              onClick={() => onToggleBookmark(pmid)}
              className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
              title={isBookmarked ? "북마크 해제" : "북마크"}
            >
              <Bookmark
                className={`w-4 h-4 ${
                  isBookmarked ? "fill-primary text-primary" : ""
                }`}
              />
            </button>
          )}
          <a
            href={pubmedUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackView(pmid)}
            className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground"
            title="PubMed"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
}
